import { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Header, HStack, IconCloseSkinny, VStack } from '@direct-frontend/components';
import { logEventString } from '@adv-frontend/yastore-modules/logger';
import { getDashboardAlertsDataProvider, resolveDashboardAlert } from '#entities/dashboardAlert';
import { type OpenApiSchemaAlert } from '#shared/api/schema';
import { HorizontalLine } from '#shared/components/HorizontalLine';
import { InfiniteScroll } from '#shared/components/InfiniteScroll';
import { showError } from '#shared/lib/toast';
import { DashboardAlert, DashboardAlertStub } from '../DashboardAlert';
import styles from './DashboardAlertsList.module.css';

type DashboardAlertsListProps = {
    alerts?: readonly OpenApiSchemaAlert[];
    onAction: VoidFunction;
    onClose: VoidFunction;
};

export const DashboardAlertsList = ({ alerts, onAction, onClose }: DashboardAlertsListProps) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);

    const handleScroll = () => {
        if (listRef.current) {
            setIsScrolled(listRef.current.scrollTop > 0);
        }
    };

    const dataProvider = useMemo(() => getDashboardAlertsDataProvider({}), []);

    const handleMarkAsRead = useCallback(async (alertId: string) => {
        try {
            await resolveDashboardAlert({ id: alertId });

            logEventString('DashboardAlerts.Message.MarkAsRead', 'true', { alertId });
        } catch (error) {
            showError({ label: 'Произошла ошибка при изменении статуса уведомления' }, error);
        }
    }, []);

    const mapAlertProps = (alert: OpenApiSchemaAlert) => {
        return {
            alert,
            onAction,
            onRead: () => handleMarkAsRead(alert.id),
        };
    };

    return (
        <>
            <HStack className={styles.header} alignItems="center" justifyContent="space-between">
                <Header level="h2">Уведомления об ошибках</Header>
                <Button
                    aria-label="Закрыть уведомления об ошибках"
                    color="text"
                    icon={IconCloseSkinny}
                    onClick={onClose}
                    size="m"
                    type="button"
                />
            </HStack>

            {isScrolled && <HorizontalLine />}

            <VStack className={styles.scrollable}>
                {alerts === undefined ? (
                    <InfiniteScroll
                        stub={<DashboardAlertStub />}
                        ref={listRef}
                        onScroll={handleScroll}
                        dataProvider={dataProvider}
                    >
                        {(alert) => <DashboardAlert {...mapAlertProps(alert)} />}
                    </InfiniteScroll>
                ) : (
                    alerts.map((alert) => (
                        <DashboardAlert key={`${alert.id}:${alert.created_at}`} {...mapAlertProps(alert)} />
                    ))
                )}
            </VStack>
        </>
    );
};
